import amaLog, { amaWarn } from '../helpers/log';
import { isDesktop } from '../config/device';
import { getDocumentBounds, getScrollPosition } from '../helpers/geometry';

type DeferredAdRunner = () => void;

type BatchViewportDistance = {
	desktop: number;
	mobile: number;
};

const FAST_SCROLL_DELAY_MS = 400;
const MAX_FAST_SCROLL_DEFER_MS = 2000;

// Detects if the user is scrolling fast enough to warrant delaying ad init
const isFastScroll = (() => {
	const FAST_THRESHOLD = 500;
	const ACTIVE_TIME = 400;
	type ScrollRecord = { position: number; time: number };
	const getScrollPosition = () => window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
	let scrolls: ScrollRecord[] = [];

	window.addEventListener('scroll', () => {
		const now = Math.round(performance.now());
		scrolls = scrolls
			.filter((item) => now - item.time <= ACTIVE_TIME)
			.concat({ position: getScrollPosition(), time: now });
	}, { passive: true });

	return (): boolean => {
		const now = Math.round(performance.now());
		const currentPosition = getScrollPosition();
		scrolls = scrolls.filter((item) => now - item.time <= ACTIVE_TIME);
		return scrolls.some((item) => Math.abs(currentPosition - item.position) >= FAST_THRESHOLD);
	};
})();

class DeferredLoadingManager {
	private static readonly DEFAULT_BATCH_VIEWPORT_DISTANCE: BatchViewportDistance = { desktop: 3, mobile: 4 };

	private readonly name: string;
	private readonly batchSize: number = 5;
	private readonly flushIntervalMs: number = 25;
	private batchViewportDistance: BatchViewportDistance = { ...DeferredLoadingManager.DEFAULT_BATCH_VIEWPORT_DISTANCE };

	private deferredAds: DeferredAdRunner[] = [];
	private deferredAdsTimer: number | null = null;
	private retryTimers: Set<number> = new Set();
	private deferredAdsObserver: IntersectionObserver | null = null;
	private deferredObservedAds: Map<Element, DeferredAdRunner> = new Map();

	constructor(name: string = 'Default') {
		this.name = name;
	}

	public setBatchViewportDistance(distance: BatchViewportDistance): void {
		this.batchViewportDistance = {
			desktop: Math.max(0, distance.desktop),
			mobile: Math.max(0, distance.mobile),
		};

		const currentObserved = Array.from(this.deferredObservedAds.entries());
		this.deferredAdsObserver?.disconnect();
		this.deferredAdsObserver = null;

		currentObserved.forEach(([element, runner]) => {
			this.observeDeferredAd(element as HTMLElement, runner);
		});

		amaLog(`DEFER[${this.name}] - Updated batch viewport distance`, this.batchViewportDistance);
	}

	public resetBatchViewportDistance(): void {
		this.setBatchViewportDistance(DeferredLoadingManager.DEFAULT_BATCH_VIEWPORT_DISTANCE);
	}

	public reset(): void {
		if (this.deferredAdsTimer !== null) {
			clearTimeout(this.deferredAdsTimer);
			this.deferredAdsTimer = null;
		}

		this.retryTimers.forEach((timerId) => clearTimeout(timerId));
		this.retryTimers.clear();

		this.deferredAds = [];
		this.deferredAdsObserver?.disconnect();
		this.deferredAdsObserver = null;
		this.deferredObservedAds.clear();
	}

	public schedule(runDeferredAd: DeferredAdRunner, div?: HTMLElement): void {
		if (this.shouldBatchDeferredAd(div)) {
			amaLog(`DEFER[${this.name}] - Scheduling for batch processing.`, div?.id);
			this.enqueueDeferredAd(runDeferredAd, div);
			return;
		}

		const observedRunner = this.createDeferredRunner(runDeferredAd, div);
		amaLog(`DEFER[${this.name}] - Scheduling for one-by-one observation loading.`, { divId: div?.id });
		this.observeDeferredAd(div, observedRunner);
	}

	private shouldBatchDeferredAd(div?: HTMLElement): boolean {
		return !div;
	}

	private getObserverRootMargin(): string {
		const viewportDistance = isDesktop() ? this.batchViewportDistance.desktop : this.batchViewportDistance.mobile;
		return `0px 0px ${viewportDistance * 110}% 0px`;
	}

	private isWithinObserverRange(element: Element): boolean {
		const bounds = getDocumentBounds(element);
		const scrollTop = getScrollPosition();
		const windowHeight = window.innerHeight || document.documentElement.clientHeight;
		const viewportDistance = isDesktop() ? this.batchViewportDistance.desktop : this.batchViewportDistance.mobile;
		const rootMarginPx = windowHeight * viewportDistance * 1.1;
		return bounds.top <= scrollTop + windowHeight + rootMarginPx && bounds.bottom >= scrollTop;
	}

	private createDeferredRunner(fn: DeferredAdRunner, div?: HTMLElement): DeferredAdRunner {
		let pending = true;

		return () => {
			if (!pending) return;
			pending = false;
			this.cleanupDeferredObservation(div);
			fn();
		};
	}

	private scheduleRetry(fn: () => void, delayMs: number): void {
		const timerId = window.setTimeout(() => {
			this.retryTimers.delete(timerId);
			fn();
		}, delayMs);
		this.retryTimers.add(timerId);
	}

	private enqueueDeferredAd(fn: DeferredAdRunner, div?: HTMLElement): DeferredAdRunner {
		const runner = this.createDeferredRunner(fn, div);

		amaLog(`DEFER[${this.name}] - Enqueuing deferred ad ${div?.id} Batch size: ${this.deferredAds.length + 1}.`);

		this.deferredAds.push(runner);

		if (this.deferredAdsTimer === null) {
			this.deferredAdsTimer = window.setTimeout(() => {
				this.flushDeferredAds();
			}, this.flushIntervalMs);
		}

		return runner;
	}

	private observeDeferredAd(div: HTMLElement | undefined, deferredRunner: DeferredAdRunner): void {
		if (typeof deferredRunner !== 'function') {
			amaWarn(`DEFER[${this.name}] - Invalid deferredRunner provided, expected function.`, {
				divId: div?.id,
				deferredRunner: deferredRunner,
			});
			return;
		}
		if (!div || typeof window.IntersectionObserver === 'undefined') {
			amaLog(`DEFER[${this.name}] - IntersectionObserver unavailable, or div missing. Running ad immediately.`, { divId: div?.id });
			deferredRunner();
			return;
		}

		if (!this.deferredAdsObserver) {
			const runOrDelay = (
				target: Element,
				runner: DeferredAdRunner,
				deadline: number = performance.now() + MAX_FAST_SCROLL_DEFER_MS,
			) => {
				if (!target.isConnected) {
					amaLog(`DEFER[${this.name}] - Element no longer in DOM, skipping ad`, { divId: target.id });
					this.cleanupDeferredObservation(target);
				} else if (!this.isWithinObserverRange(target) && performance.now() < deadline) {
					// IntersectionObserver only re-fires on a threshold crossing, so if our own range
					// check disagrees with the browser's intersecting verdict, don't wait indefinitely
					// for a re-entry that may never come - retry for a bounded time, then trust the observer.
					amaLog(`DEFER[${this.name}] - Element moved out of observer range, waiting for re-entry`, { divId: target.id });
					this.scheduleRetry(() => runOrDelay(target, runner, deadline), FAST_SCROLL_DELAY_MS);
				} else if (isFastScroll() && performance.now() < deadline) {
					amaLog(`DEFER[${this.name}] - Fast scroll detected, delaying ad`, { divId: target.id });
					this.scheduleRetry(() => runOrDelay(target, runner, deadline), FAST_SCROLL_DELAY_MS);
				} else {
					amaLog(`DEFER[${this.name}] - Observer triggered for deferred ad`, { divId: target.id });
					runner();
				}
			};
			this.deferredAdsObserver = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						if (!entry.isIntersecting) return;

						const runner = this.deferredObservedAds.get(entry.target);
						if (!runner) return;

						runOrDelay(entry.target, runner);
					});
				},
				{
					root: null,
					rootMargin: this.getObserverRootMargin(),
					threshold: 0.01,
				},
			);
		}

		this.deferredObservedAds.set(div, deferredRunner);
		this.deferredAdsObserver.observe(div);
	}

	private cleanupDeferredObservation(element?: Element): void {
		if (!element) return;

		this.deferredAdsObserver?.unobserve(element);
		this.deferredObservedAds.delete(element);
	}

	public flushDeferredAds(): void {
		if (this.deferredAdsTimer !== null) {
			clearTimeout(this.deferredAdsTimer);
			this.deferredAdsTimer = null;
		}

		if (!this.deferredAds.length) {
			return;
		}

		if (isFastScroll()) {
			amaLog(`DEFER[${this.name}] - Fast scroll detected, delaying batch flush`);
			this.deferredAdsTimer = window.setTimeout(() => this.flushDeferredAds(), FAST_SCROLL_DELAY_MS);
			return;
		}

		const deferredAdsToRun = this.deferredAds.splice(0, this.batchSize);

		amaLog(`DEFER[${this.name}] - Processing deferred ads`, { deferredAds: deferredAdsToRun.length });

		deferredAdsToRun.forEach((deferredAd) => deferredAd());

		if (this.deferredAds.length) {
			this.deferredAdsTimer = window.setTimeout(() => {
				this.flushDeferredAds();
			}, this.flushIntervalMs);
		}
	}
}

export const deferredGptLoadingManager = new DeferredLoadingManager('GPT');

// Dedicated queue for first-wave non-lazy batching to avoid mixing with deferred placement scheduling.
export const deferredFirstWaveBatchManager = new DeferredLoadingManager('FirstWaveBatch');
